import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
} from "react";
import type { OpenedTrade } from "@/vybe-trader-client/opened-trades/types";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import type {
  TradeOverlayItem,
  TradeOverlayLineData,
  TradeOverlayLineKind,
  TradeOverlayQuote,
} from "./types";
import {
  buildLineLabel,
  buildLineLeftStatus,
  computeLeftStatusForPrice,
  getLineColor,
  getLineWidth,
  normalizeTradeOverlayItems,
  shouldDim,
  type TradeOverlayPnlContext,
} from "./utils";
import {
  createTradeLevelOverlay,
  registerTradeLevelOverlay,
  updateTradeLevelOverlay,
} from "./overlays/tradeLevelOverlay";
import {
  defaultTradeOverlayTheme,
  type TradeOverlayTheme,
} from "./theme";

interface UseChartTradeOverlayProps {
  chartInstanceRef: MutableRefObject<any>;
  selectedSymbol: { currency: string } | null;
  selectedTimeframe: string;
  trades: OpenedTrade[];
  currentQuote?: TradeOverlayQuote | null;
  selectedTradeTicket?: string | null;
  showTradePositions: boolean;
  showPendingOrders: boolean;
  showSlTpLevels: boolean;
  isPipsVisible: boolean;
  theme?: Partial<TradeOverlayTheme>;
  onTradeClick?: (ticket: string) => void;
  onTradeDoubleClick?: (ticket: string) => void;
  onTradeClose?: (ticket: string) => void;
  onPendingEntryPriceChange?: (ticket: string, price: number) => void;
  onTpClose?: (ticket: string) => void;
  onSlClose?: (ticket: string) => void;
  onTpPriceChange?: (ticket: string, price: number) => void;
  onSlPriceChange?: (ticket: string, price: number) => void;
  onTradeInteraction?: () => void;
  /** Investor (read-only) session: lines render without close chips or drag handles. */
  readOnly?: boolean;
}

type OverlayIdsByKind = Partial<Record<TradeOverlayLineKind, string>>;

const MANAGED_OVERLAY_NAMES = new Set([
  "vybeTradeLevel",
  "customTradeLine",
  "customTradePendingOrder",
  "simpleSlTpLine",
  "customSlTpLine",
]);

export function useChartTradeOverlay({
  chartInstanceRef,
  selectedSymbol,
  selectedTimeframe,
  trades,
  currentQuote,
  selectedTradeTicket,
  showTradePositions,
  showPendingOrders,
  showSlTpLevels,
  isPipsVisible,
  theme,
  onTradeClick,
  onTradeDoubleClick,
  onTradeClose,
  onPendingEntryPriceChange,
  onTpClose,
  onSlClose,
  onTpPriceChange,
  onSlPriceChange,
  onTradeInteraction,
  readOnly = false,
}: UseChartTradeOverlayProps) {
  const overlayIdsRef = useRef<Map<string, OverlayIdsByKind>>(new Map());
  /**
   * Tracks `${ticket}:${lineKind}` keys that are mid-drag, so the per-tick
   * overlay-sync effect skips overriding the dragged overlay (which would
   * otherwise snap the line back to the server-side price).
   */
  const draggingLinesRef = useRef<Set<string>>(new Set());
  const currentTheme = useMemo(
    () => ({ ...defaultTradeOverlayTheme, ...theme }),
    [theme]
  );
  // Note: we deliberately do NOT subscribe to the whole `byCurrency` maps of
  // `useSubscribedSymbolsStore` / `useSymbolsStore`. Those maps are rebuilt
  // (new reference) on EVERY quote tick across ALL subscribed symbols, which
  // would force the chart to re-render on every unrelated tick. Instead we
  // read fresh snapshots via `getState()` inside the memo below; the memo's
  // change signal is the selected symbol's `currentQuote` (already a dep), so
  // cross-rate PnL conversion is refreshed on each chart-symbol tick - which
  // is more than frequent enough in practice.
  const accountCurrency = useAccountInformationStore(
    (state) => state.account?.currency || "USD"
  );

  // If there is nothing to render and no trades to track, we can skip the
  // heavy overlay-sync effect body entirely.
  const overlayWorkEnabled =
    trades.length > 0 &&
    (showTradePositions || showPendingOrders || showSlTpLevels);

  const overlayItems = useMemo(() => {
    if (trades.length === 0) {
      return [] as ReturnType<typeof normalizeTradeOverlayItems>;
    }
    return normalizeTradeOverlayItems(
      trades,
      selectedSymbol?.currency,
      currentQuote,
      {
        accountCurrency,
        symbols: useSymbolsStore.getState().byCurrency,
        quotes: useSubscribedSymbolsStore.getState().byCurrency,
      }
    );
  }, [trades, selectedSymbol?.currency, currentQuote, accountCurrency]);

  const removeOverlay = useCallback(
    (overlayId?: string) => {
      if (!overlayId || !chartInstanceRef.current) {
        return;
      }

      try {
        chartInstanceRef.current.removeOverlay(overlayId);
      } catch (error) {
        console.error("Failed to remove trade overlay:", error);
      }
    },
    [chartInstanceRef]
  );

  const forceCleanupTrade = useCallback(
    (ticket: string | number) => {
      const ticketKey = ticket.toString();
      const ids = overlayIdsRef.current.get(ticketKey);

      if (ids) {
        Object.values(ids).forEach(removeOverlay);
        overlayIdsRef.current.delete(ticketKey);
      }

      removeTradeOverlaysByTicket(chartInstanceRef.current, ticketKey);
    },
    [chartInstanceRef, removeOverlay]
  );

  const removeAllManagedOverlays = useCallback(() => {
    overlayIdsRef.current.forEach((ids) => {
      Object.values(ids).forEach(removeOverlay);
    });
    overlayIdsRef.current.clear();
    draggingLinesRef.current.clear();
    removeTradeOverlaysByName(chartInstanceRef.current);
  }, [chartInstanceRef, removeOverlay]);

  const handlePendingEntryDragStart = useCallback((ticket: string) => {
    draggingLinesRef.current.add(getLineKey(ticket, "entry"));
  }, []);

  const handlePendingEntryDragEnd = useCallback((ticket: string) => {
    draggingLinesRef.current.delete(getLineKey(ticket, "entry"));
  }, []);

  const handleTpDragStart = useCallback((ticket: string) => {
    draggingLinesRef.current.add(getLineKey(ticket, "tp"));
  }, []);

  const handleTpDragEnd = useCallback((ticket: string) => {
    draggingLinesRef.current.delete(getLineKey(ticket, "tp"));
  }, []);

  const handleSlDragStart = useCallback((ticket: string) => {
    draggingLinesRef.current.add(getLineKey(ticket, "sl"));
  }, []);

  const handleSlDragEnd = useCallback((ticket: string) => {
    draggingLinesRef.current.delete(getLineKey(ticket, "sl"));
  }, []);

  /**
   * Mirrors a TP/SL drag-in-progress price onto the trade's *entry* overlay
   * so its "Position Ratio X:Y" middle label refreshes live. Each line is a
   * separate klinecharts overlay; without this hop the entry overlay would
   * still hold the pre-drag TP/SL value until the modify round-trip lands.
   *
   * We mutate the entry overlay's `extendData` in place instead of calling
   * `overrideOverlay`. klinecharts repaints the whole canvas while a drag is
   * active, so the entry overlay's `createPointFigures` re-runs on the
   * next frame and picks up the new value with no extra redraw cost.
   */
  const handleLiveTpSlChange = useCallback(
    (ticket: string, lineKind: "tp" | "sl", price: number) => {
      const ids = overlayIdsRef.current.get(ticket);
      const entryId = ids?.entry;
      if (!entryId) return;

      const chart = chartInstanceRef.current;
      const entry = chart?.getOverlayById?.(entryId);
      const extendData = entry?.extendData as
        | TradeOverlayLineData
        | undefined;
      if (!extendData) return;

      if (lineKind === "tp") {
        extendData.takeProfit = price;
      } else {
        extendData.stopLoss = price;
      }
    },
    [chartInstanceRef]
  );

  useEffect(() => {
    registerTradeLevelOverlay();

    return () => {
      removeAllManagedOverlays();
    };
  }, [removeAllManagedOverlays]);

  useEffect(() => {
    removeAllManagedOverlays();
  }, [selectedSymbol?.currency, selectedTimeframe, removeAllManagedOverlays]);

  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart || !selectedSymbol) {
      removeAllManagedOverlays();
      return;
    }

    // Fast path: no trades or all overlay features off. Make sure any
    // previously-drawn overlays are removed, then bail out before the heavy
    // per-tick diffing loop.
    if (!overlayWorkEnabled) {
      if (overlayIdsRef.current.size > 0) {
        removeAllManagedOverlays();
      }
      return;
    }

    const activeTickets = new Set<string>();
    const labelOffsets = buildLabelOffsets(overlayItems, {
      showTradePositions,
      showPendingOrders,
      showSlTpLevels,
      selectedTradeTicket,
    });

    // Snapshot the cross-rate context once per overlay rebuild. The drag
    // handler closures below capture this so they can recompute projected
    // P&L at any intermediate price without re-reading the stores on every
    // mouse-move tick (which would also bypass the deliberate
    // "no per-tick subscription" optimization above).
    const pnlContext: TradeOverlayPnlContext = {
      accountCurrency,
      symbols: useSymbolsStore.getState().byCurrency,
      quotes: useSubscribedSymbolsStore.getState().byCurrency,
    };

    overlayItems.forEach((item) => {
      const ticket = item.ticket.toString();
      activeTickets.add(ticket);

      const existingIds = overlayIdsRef.current.get(ticket) ?? {};
      const nextIds: OverlayIdsByKind = { ...existingIds };
      const desiredKinds = getDesiredLineKinds(item, {
        showTradePositions,
        showPendingOrders,
        showSlTpLevels,
        selectedTradeTicket,
      });

      (["entry", "tp", "sl"] as TradeOverlayLineKind[]).forEach((lineKind) => {
        if (!desiredKinds.has(lineKind)) {
          removeOverlay(nextIds[lineKind]);
          delete nextIds[lineKind];
          return;
        }

        const lineData = buildLineData(
          item,
          lineKind,
          selectedTradeTicket ?? null,
          isPipsVisible,
          currentQuote,
          currentTheme,
          labelOffsets.get(getLineKey(item.ticket, lineKind)) ?? 0
        );

        if (!lineData) {
          removeOverlay(nextIds[lineKind]);
          delete nextIds[lineKind];
          return;
        }
        if (readOnly) lineData.readOnly = true;

        const callbacks = {
          onLineClick: onTradeClick,
          onLineDoubleClick: onTradeDoubleClick,
          onLineClose: onTradeClose,
          onPendingEntryDragStart: handlePendingEntryDragStart,
          onPendingEntryDragEnd: handlePendingEntryDragEnd,
          onPendingEntryPriceChange,
          onTpClose,
          onSlClose,
          onTpDragStart: handleTpDragStart,
          onTpDragEnd: handleTpDragEnd,
          onTpPriceChange,
          onSlDragStart: handleSlDragStart,
          onSlDragEnd: handleSlDragEnd,
          onSlPriceChange,
          onTradeInteraction,
          recomputeLeftStatus: (price: number) =>
            computeLeftStatusForPrice(
              item,
              lineKind,
              price,
              isPipsVisible,
              pnlContext
            ),
          onLiveTpSlChange: handleLiveTpSlChange,
        };

        // Skip rebuilding any line whose own drag is in progress, AND skip
        // rebuilding the entry line while TP or SL of the same trade is
        // mid-drag. Otherwise a quote tick during the drag would reset the
        // entry overlay's `takeProfit` / `stopLoss` back to the (still
        // un-modified) store value and revert the live "Position Ratio"
        // label until the next mouse-move.
        const selfDragging = draggingLinesRef.current.has(
          getLineKey(item.ticket, lineKind)
        );
        const entryClobberFromTpSlDrag =
          lineKind === "entry" &&
          (draggingLinesRef.current.has(getLineKey(item.ticket, "tp")) ||
            draggingLinesRef.current.has(getLineKey(item.ticket, "sl")));

        if (selfDragging || entryClobberFromTpSlDrag) {
          return;
        }

        if (nextIds[lineKind]) {
          updateTradeLevelOverlay(chart, nextIds[lineKind]!, lineData, callbacks);
        } else {
          const overlayId = createTradeLevelOverlay(chart, lineData, callbacks);

          if (overlayId) {
            nextIds[lineKind] = overlayId;
          }
        }
      });

      if (Object.keys(nextIds).length > 0) {
        overlayIdsRef.current.set(ticket, nextIds);
      } else {
        overlayIdsRef.current.delete(ticket);
      }
    });

    overlayIdsRef.current.forEach((ids, ticket) => {
      if (!activeTickets.has(ticket)) {
        Object.values(ids).forEach(removeOverlay);
        overlayIdsRef.current.delete(ticket);
      }
    });
  }, [
    chartInstanceRef,
    currentQuote,
    currentTheme,
    handlePendingEntryDragEnd,
    handlePendingEntryDragStart,
    handleTpDragStart,
    handleTpDragEnd,
    handleSlDragStart,
    handleSlDragEnd,
    handleLiveTpSlChange,
    isPipsVisible,
    onTradeClick,
    onTradeClose,
    onTradeDoubleClick,
    onPendingEntryPriceChange,
    onTpClose,
    onSlClose,
    onTpPriceChange,
    onSlPriceChange,
    onTradeInteraction,
    overlayItems,
    overlayWorkEnabled,
    readOnly,
    removeAllManagedOverlays,
    removeOverlay,
    selectedSymbol,
    selectedTradeTicket,
    showPendingOrders,
    showSlTpLevels,
    showTradePositions,
  ]);

  return { forceCleanupTrade };
}

function getDesiredLineKinds(
  item: TradeOverlayItem,
  options: {
    showTradePositions: boolean;
    showPendingOrders: boolean;
    showSlTpLevels: boolean;
    selectedTradeTicket?: string | null;
  }
): Set<TradeOverlayLineKind> {
  const desiredKinds = new Set<TradeOverlayLineKind>();

  if (item.isPending ? options.showPendingOrders : options.showTradePositions) {
    desiredKinds.add("entry");
  }

  // Show TP/SL for both selected and non-selected trades. When selected, the
  // overlay just switches to a "solid" style with solid label backgrounds;
  // the line shape (partial, anchored at the trade's open time) stays the
  // same. The legacy full-width interactive SL/TP overlay is now suppressed
  // when the trade already has SL/TP set (see `useInteractiveSlNew` /
  // `useInteractiveTpNew`).
  if (options.showSlTpLevels && item.tp) {
    desiredKinds.add("tp");
  }

  if (options.showSlTpLevels && item.sl) {
    desiredKinds.add("sl");
  }

  return desiredKinds;
}

function buildLineData(
  item: TradeOverlayItem,
  lineKind: TradeOverlayLineKind,
  selectedTradeTicket: string | null,
  isPipsVisible: boolean,
  quote: TradeOverlayQuote | null | undefined,
  theme: TradeOverlayTheme,
  labelOffsetY: number
): TradeOverlayLineData | null {
  const price =
    lineKind === "tp" ? item.tp : lineKind === "sl" ? item.sl : item.openPrice;

  if (!price) {
    return null;
  }

  const selected = selectedTradeTicket === item.ticket.toString();
  // Selected trades use a solid line regardless of kind so the focused trade
  // stands out from the dashed TP/SL and dotted pending entries of the rest.
  const lineStyle: TradeOverlayLineData["lineStyle"] = selected
    ? "solid"
    : lineKind === "entry"
      ? item.isPending
        ? "dotted"
        : "solid"
      : "dashed";
  const color = getLineColor(item, lineKind, selectedTradeTicket, theme);

  return {
    ticket: item.ticket.toString(),
    symbol: item.symbol,
    lineKind,
    direction: item.direction,
    orderType: item.orderType,
    orderTypeLabel: item.orderTypeLabel,
    isPending: item.isPending,
    lots: item.lots,
    price,
    openPrice: item.openPrice,
    openTime: item.openTime,
    label: buildLineLabel(item, lineKind),
    leftStatus: buildLineLeftStatus(item, lineKind, isPipsVisible, quote),
    labelOffsetY,
    color,
    backgroundColor: color,
    textColor: theme.labelTextColor,
    lineStyle,
    lineWidth: getLineWidth(item.ticket, selectedTradeTicket),
    selected,
    dimmed: shouldDim(item.ticket, selectedTradeTicket),
    digits: item.digits,
    takeProfit: item.tp,
    stopLoss: item.sl,
  };
}

function buildLabelOffsets(
  items: TradeOverlayItem[],
  options: {
    showTradePositions: boolean;
    showPendingOrders: boolean;
    showSlTpLevels: boolean;
    selectedTradeTicket?: string | null;
  }
): Map<string, number> {
  const groups = new Map<string, Array<{ ticket: number; lineKind: TradeOverlayLineKind }>>();

  items.forEach((item) => {
    const kinds = getDesiredLineKinds(item, options);
    kinds.forEach((lineKind) => {
      const price =
        lineKind === "tp" ? item.tp : lineKind === "sl" ? item.sl : item.openPrice;
      if (!price) return;

      const groupKey = price.toFixed(Math.max(0, Math.min(item.digits || 5, 8)));
      const group = groups.get(groupKey) ?? [];
      group.push({ ticket: item.ticket, lineKind });
      groups.set(groupKey, group);
    });
  });

  const offsets = new Map<string, number>();
  groups.forEach((group) => {
    if (group.length <= 1) {
      return;
    }

    group
      .sort((a, b) => a.ticket - b.ticket)
      .forEach((entry, index) => {
        offsets.set(
          getLineKey(entry.ticket, entry.lineKind),
          (index - (group.length - 1) / 2) * 18
        );
      });
  });

  return offsets;
}

function getLineKey(ticket: string | number, lineKind: TradeOverlayLineKind) {
  return `${ticket}:${lineKind}`;
}

function removeTradeOverlaysByTicket(chartInstance: any, ticket: string) {
  if (!chartInstance) {
    return;
  }

  try {
    const overlayMap = chartInstance.getOverlaysByPaneId?.();
    if (!(overlayMap instanceof Map)) {
      return;
    }

    overlayMap.forEach((paneOverlays) => {
      if (!(paneOverlays instanceof Map)) {
        return;
      }

      paneOverlays.forEach((overlay, overlayId) => {
        if (overlay?.extendData?.ticket?.toString() === ticket) {
          chartInstance.removeOverlay(overlayId);
        }
      });
    });
  } catch (error) {
    console.error("Failed to cleanup trade overlays by ticket:", error);
  }
}

function removeTradeOverlaysByName(chartInstance: any) {
  if (!chartInstance) {
    return;
  }

  try {
    const overlayMap = chartInstance.getOverlaysByPaneId?.();
    if (!(overlayMap instanceof Map)) {
      return;
    }

    overlayMap.forEach((paneOverlays) => {
      if (!(paneOverlays instanceof Map)) {
        return;
      }

      paneOverlays.forEach((overlay, overlayId) => {
        if (MANAGED_OVERLAY_NAMES.has(overlay?.name)) {
          chartInstance.removeOverlay(overlayId);
        }
      });
    });
  } catch (error) {
    console.error("Failed to cleanup managed trade overlays:", error);
  }
}
